import {
  ClickedProductAfterSearchEvent,
  ConvertedObjectIDsAfterSearchEvent,
  useSearchInsights,
} from '@/hooks/useSearchInsights'
import { useHeaderSearchState } from './hooks/useHeaderSearchState'
import type {
  BaseFilterValue,
  FilterForm,
} from '@/components/FiltersDrawer/types'
import { useSearch } from './useSearch'
import { useFeatureFlags } from '@/hooks/useFeatureFlags'
import { useEffect, useState } from 'react'
import { mapValuesToFacets } from '@/components/FiltersDrawer/utils'
import { useSearchParamsSync } from '@/pages/Collection/useSearchParamsSync'
import { algoliaDefaultValues } from '@/services/algolia'
import type { HeaderSearchProps } from './types'
import { useFilterSections } from '@/components/FiltersDrawer/hooks'
import { useTranslation } from 'react-i18next'
import { Search } from '../Search'
import { FiltersDrawer } from '@/components/FiltersDrawer/FiltersDrawer'
import { StandardProductCard } from '@/features/product/components/StandardProductCard'
import { useAuth } from '@lounge-fe/auth'
import { useSearchContext } from '@/features/navigation/components/Navigation/SearchProvider'
import { useDebounce } from '@lounge-fe/ui-kit'
import { useRecentlyAddedProducts } from '@/features/cart/hooks/useRecentlyAddedProducts'

export const HeaderSearch: React.FC<HeaderSearchProps> = ({
  searchTiles: searchTilesFromProps,
  suggestedSearchTermFallbacks,
}) => {
  const { status: authStatus } = useAuth()
  const searchTiles = (searchTilesFromProps ?? []).filter(({ image }) =>
    Boolean(image),
  )

  const { defaultValues, updateSearchParams } = useSearchParamsSync<FilterForm>(
    {
      defaultValues: algoliaDefaultValues,
    },
  )
  const [filters, setFilters] = useState(mapValuesToFacets(defaultValues))
  const { getFlag } = useFeatureFlags()
  const isSearchFiltersEnabled = getFlag('enable_search_filters')

  const {
    query,
    isSearchOpen,
    availableFilters,
    hits,
    isLastPage,
    showMore,
    nbHits,
    suggestedSearchTerms,
    onSearchChange,
  } = useSearch({
    isSearchFiltersEnabled,
    filters,
    suggestedSearchTermFallbacks: suggestedSearchTermFallbacks ?? [],
  })

  const { search } = useSearchContext()
  const debouncedSearch = useDebounce(search, 500)

  // This is a work around to avoid needing to define the useSearch hook at the Header level
  // and keep all of this components logic defined at a higher level
  // in order for the SearchProvider to work across the navigation tree structure.
  useEffect(() => {
    onSearchChange(debouncedSearch)
  }, [debouncedSearch])

  const { clickedProductAfterSearch, convertedObjectIDsAfterSearch } =
    useSearchInsights()

  const facets: { [key: string]: BaseFilterValue[] } = availableFilters ?? {}
  const { filterSections } = useFilterSections(facets)

  const { t } = useTranslation()

  const handleFiltersChanged = (values: FilterForm) => {
    updateSearchParams(values)
    setFilters(mapValuesToFacets(values))
  }

  const { clearRecentlyAddedProductIds, recentlyAddedProductIds } =
    useRecentlyAddedProducts()

  useHeaderSearchState()

  return (
    <Search
      isFull={query.length > 0}
      isOpen={isSearchOpen}
      className={!query.length ? 'py-2 xl:pt-0' : undefined}
    >
      {query.length > 0 && (
        <>
          {isSearchFiltersEnabled ? (
            <>
              <div className="text-center w-full py-2">
                <Search.Stats query={query} count={nbHits} />
              </div>
              <div className="md:flex-row justify-between sticky z-[60] bg-white top-16 md:top-20 md:py-4">
                <FiltersDrawer
                  title={t('action.filter')}
                  drawerTitle={t('action.filter_by')}
                  className="py-4 md:border-t-0 md:pt-0"
                  filterSections={filterSections}
                  defaultValues={algoliaDefaultValues}
                  onSubmit={handleFiltersChanged}
                />
              </div>
            </>
          ) : (
            <div className="mb-8">
              <Search.Stats query={query} count={nbHits} />
            </div>
          )}

          <Search.Grid>
            {hits.map((hit, idx) => (
              <StandardProductCard
                product={hit}
                key={hit.id}
                useImageCarousel
                useQuickAdd
                showExclusiveQuickAdd
                onClick={() => {
                  if (hit.objectID) {
                    clickedProductAfterSearch({
                      objectID: hit.objectID,
                      eventName: ClickedProductAfterSearchEvent.Search,
                      queryID: hit.queryID,
                      position: idx + 1,
                    })
                  }
                }}
                onQuickAdd={() => {
                  if (hit.queryID && hit.objectID) {
                    convertedObjectIDsAfterSearch({
                      objectID: hit.objectID,
                      eventName: ConvertedObjectIDsAfterSearchEvent.Search,
                      queryID: hit.queryID,
                    })
                  }
                }}
                showWishlist={authStatus === 'authenticated' ? true : false}
              />
            ))}
          </Search.Grid>
          {!isLastPage && (
            <Search.LoadMore
              currentHits={hits.length}
              totalHits={nbHits}
              onClick={() => showMore()}
            />
          )}
        </>
      )}
      {query.length === 0 && (
        <Search.Menu
          tiles={searchTiles.map((tile) => ({
            href: tile.link,
            image: tile.image,
            title: tile.title,
          }))}
        >
          <Search.Recommendations suggestedSearchTerms={suggestedSearchTerms} />
        </Search.Menu>
      )}
    </Search>
  )
}
